import { palierEffectif, prixCatalogue } from './mrr'
import { calculerTarif, MAX_VEHICULES_SUPP, PERIODES, type Tarif } from './tarification'
import type { Changement, Organisation, PalierCode, PeriodeFacturation, Plan, StatutAbonnement } from './types'

/**
 * Activation / modification manuelle d'un abonnement — logique pure, testée.
 * La règle côté base est `platform_definir_abonnement()` (0019, étendue en 0027
 * : véhicules supplémentaires et période de facturation) : note obligatoire,
 * refus pour l'organisation opératrice, journal en ajout seul.
 */

export const LIBELLE_PERIODE: Record<PeriodeFacturation, string> = {
  monthly: 'Mensuel',
  annual: 'Annuel (10 mois payés pour 12)',
}
export { PERIODES, MAX_VEHICULES_SUPP }

/** Un palier qui ne vend pas de véhicules supplémentaires (Business : le plafond se négocie au devis). */
export function proposeDesSupplementaires(plans: readonly Plan[] | null, tier: PalierCode): boolean {
  if (!plans) return tier !== 'business'
  return plans.find((p) => p.tier === tier)?.extra_vehicle_price_fcfa != null
}

export const PALIERS: readonly PalierCode[] = ['starter', 'pro', 'business']
export const STATUTS: readonly StatutAbonnement[] = ['trialing', 'active', 'past_due', 'cancelled']

export const LIBELLE_PALIER: Record<PalierCode, string> = {
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
}

export const LIBELLE_STATUT: Record<StatutAbonnement, string> = {
  trialing: 'Essai',
  active: 'Actif',
  past_due: 'Impayé',
  cancelled: 'Résilié',
}

export const AIDE_STATUT: Record<StatutAbonnement, string> = {
  trialing: "Période d'essai : le palier s'applique jusqu'à la date de fin, puis l'organisation retombe en Starter.",
  active: "Abonnement payé. Il n'expire pas tout seul : c'est vous qui le passez en impayé ou en résilié.",
  past_due: "Paiement en retard. L'accès au palier est conservé (délai de grâce).",
  cancelled: "Abonnement arrêté : l'organisation retombe immédiatement en Starter.",
}

export const RACCOURCIS_PROLONGATION = [1, 3, 12] as const

/* ───────────── Dates ───────────── */

/** Ajoute des mois civils ; le 31 janvier + 1 mois donne le 28 (ou 29) février, pas le 3 mars. */
export function ajouterMois(base: Date, mois: number): Date {
  const d = new Date(base.getTime())
  const jour = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() + mois)
  const dernier = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  d.setDate(Math.min(jour, dernier))
  return d
}

/** Point de départ d'une prolongation : la fin actuelle si elle est à venir, sinon aujourd'hui. */
export function basePourProlonger(finActuelle: string | null, maintenant: Date): Date {
  if (finActuelle) {
    const t = Date.parse(finActuelle)
    if (Number.isFinite(t) && t > maintenant.getTime()) return new Date(t)
  }
  return new Date(maintenant.getTime())
}

/** Date locale au format `YYYY-MM-DD` — celui de <input type="date">. */
export function enDateLocale(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const j = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${j}`
}

/** `YYYY-MM-DD` → fin de cette journée (23:59:59, heure locale), en ISO. null si la date est invalide. */
export function finDeJour(dateLocale: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateLocale)
  if (!m) return null
  const [a, mo, j] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const d = new Date(a, mo - 1, j, 23, 59, 59)
  // Refuse le 31 février, que Date « corrige » en silence en 3 mars.
  if (d.getFullYear() !== a || d.getMonth() !== mo - 1 || d.getDate() !== j) return null
  return d.toISOString()
}

/* ───────────── Saisie et validation ───────────── */

export interface Saisie {
  tier: PalierCode
  status: StatutAbonnement
  /** `YYYY-MM-DD` ou chaîne vide. */
  fin: string
  raison: string
  /** Véhicules supplémentaires (entier ≥ 0 ; 0 pour un palier qui n'en vend pas). */
  extra: number
  periode: PeriodeFacturation
}

/** L'état actuel de l'abonnement d'une organisation (null = aucun abonnement). */
export interface EtatActuel {
  tier: PalierCode
  status: StatutAbonnement | null
  finPeriode: string | null
  extra: number
  periode: PeriodeFacturation
}

export interface ErreursSaisie {
  fin?: string
  raison?: string
  extra?: string
  global?: string
}

export const RAISON_MIN = 5

export function valider(saisie: Saisie, actuel: EtatActuel, maintenant: Date, plans: readonly Plan[] | null = null): ErreursSaisie {
  const e: ErreursSaisie = {}
  if (!Number.isInteger(saisie.extra) || saisie.extra < 0) {
    e.extra = 'Indiquez un nombre entier de véhicules supplémentaires (0 si aucun).'
  } else if (saisie.extra > MAX_VEHICULES_SUPP) {
    e.extra = `Au plus ${MAX_VEHICULES_SUPP} véhicules supplémentaires : au-delà, passez au palier supérieur ou faites un devis.`
  } else if (saisie.extra > 0 && !proposeDesSupplementaires(plans, saisie.tier)) {
    e.extra = 'Les véhicules supplémentaires ne sont proposés qu’aux paliers Starter et Pro.'
  }
  const raison = saisie.raison.trim()
  if (!raison) e.raison = "Indiquez la raison du changement (c'est elle qui sert de preuve)."
  else if (raison.length < RAISON_MIN) e.raison = 'Précisez un peu plus la raison.'

  if (saisie.fin) {
    if (finDeJour(saisie.fin) === null) e.fin = 'Date invalide.'
  }
  if (saisie.status === 'trialing') {
    const fin = saisie.fin ? finDeJour(saisie.fin) : null
    if (!saisie.fin) e.fin = "Un essai a besoin d'une date de fin."
    else if (fin !== null && Date.parse(fin) <= maintenant.getTime()) {
      e.fin = "La fin d'essai doit être dans le futur — sinon l'essai est échu dès l'enregistrement."
    }
  }

  if (!e.fin && !e.raison && !e.extra) {
    const memeFin = (actuel.finPeriode ? enDateLocale(new Date(actuel.finPeriode)) : '') === saisie.fin
    if (
      actuel.status === saisie.status && actuel.tier === saisie.tier && memeFin &&
      actuel.extra === saisie.extra && actuel.periode === saisie.periode
    ) {
      e.global = "Rien ne change : modifiez le palier, le statut, la date de fin, les véhicules supplémentaires ou la facturation."
    }
  }
  return e
}

export function aDesErreurs(e: ErreursSaisie): boolean {
  return Boolean(e.fin || e.raison || e.extra || e.global)
}

/**
 * Le tarif que la saisie donnera (aperçu avant confirmation). Le prix figé de
 * l'abonnement ne vaut que pour un abonnement DÉJÀ payant resté sur le MÊME
 * palier ; sinon (essai, résilié, changement de palier) c'est le catalogue du
 * jour — comme le déclencheur `figer_prix_abonnement` (0027). null = sur devis
 * ou plans illisibles.
 */
export function tarifApresSaisie(saisie: Saisie, org: Organisation, plans: readonly Plan[] | null): Tarif | null {
  if (!plans) return null
  const catalogue = prixCatalogue(plans, saisie.tier)
  if (!catalogue) return null
  const paye = org.sub_status === 'active' || org.sub_status === 'past_due'
  const prix = paye && org.plan_tier === saisie.tier && org.prix_appliques ? org.prix_appliques : catalogue
  if (prix.base === null) return null
  const extra = prix.vehiculeSupp === null ? 0 : saisie.extra
  if (!Number.isInteger(extra) || extra < 0) return null
  return calculerTarif(prix, { vehiculesSupp: extra, boitiersActifs: org.gps_devices, periode: saisie.periode })
}

/** Ce que la base appliquera réellement une fois la saisie enregistrée. */
export function palierApresSaisie(saisie: Saisie, estOperatrice: boolean, maintenant: Date): PalierCode {
  return palierEffectif(
    {
      estOperatrice,
      palier: saisie.tier,
      statut: saisie.status,
      finPeriode: saisie.fin ? finDeJour(saisie.fin) : null,
    },
    maintenant,
  )
}

/** « Pro · Actif », « Aucun abonnement » quand l'ancien état n'existait pas. */
export function libelleEtat(tier: PalierCode | null, status: StatutAbonnement | null): string {
  if (!tier || !status) return 'Aucun abonnement'
  return `${LIBELLE_PALIER[tier]} · ${LIBELLE_STATUT[status]}`
}

/** Qui a fait le changement : « Vous », le nom de l'auteur, ou « Autre administrateur » si inconnu. */
export function libelleAuteur(changedBy: string, utilisateurId: string, auteur?: string | null): string {
  if (changedBy === utilisateurId) return 'Vous'
  return auteur?.trim() || 'Autre administrateur'
}

export function libelleChangement(c: Pick<Changement, 'old_tier' | 'old_status' | 'new_tier' | 'new_status'>): string {
  return `${libelleEtat(c.old_tier, c.old_status)} → ${libelleEtat(c.new_tier, c.new_status)}`
}

/** « 2 véhicules supp. · Annuel » — l'état des options ; chaîne vide pour un changement d'avant la 0027. */
export function libelleOptions(extra: number | null | undefined, periode: PeriodeFacturation | null | undefined): string {
  if (extra == null && periode == null) return ''
  const parties: string[] = []
  if (extra != null) parties.push(extra === 0 ? 'aucun véhicule supp.' : `${extra} véhicule${extra > 1 ? 's' : ''} supp.`)
  if (periode != null) parties.push(periode === 'annual' ? 'annuel' : 'mensuel')
  return parties.join(' · ')
}

/** Les options ont-elles changé ? Sert à ne mentionner que ce qui bouge dans le journal. */
export function optionsChangees(c: Pick<Changement, 'old_extra_vehicles' | 'new_extra_vehicles' | 'old_billing_period' | 'new_billing_period'>): boolean {
  return (c.old_extra_vehicles ?? null) !== (c.new_extra_vehicles ?? null) || (c.old_billing_period ?? null) !== (c.new_billing_period ?? null)
}

/* ───────────── Erreurs en phrases ───────────── */

function lire(e: unknown): { message: string; code: string; detail: string } {
  if (typeof e === 'string') return { message: e, code: '', detail: '' }
  if (e && typeof e === 'object') {
    const o = e as { message?: unknown; code?: unknown; details?: unknown }
    return {
      message: typeof o.message === 'string' ? o.message : '',
      code: typeof o.code === 'string' ? o.code : '',
      detail: typeof o.details === 'string' ? o.details : '',
    }
  }
  return { message: '', code: '', detail: '' }
}

/** Traduit une erreur Supabase/PostgREST de `platform_definir_abonnement` en phrase. */
export function traduireErreurAbonnement(erreur: unknown): string {
  const { message, code, detail } = lire(erreur)
  const m = message.toLowerCase()
  if (m.includes('note_required')) return "Indiquez la raison du changement : elle est obligatoire."
  if (m.includes('operator_has_no_plan')) {
    return "L'organisation opératrice n'a pas d'abonnement : c'est nous, elle n'a aucun plan à modifier."
  }
  if (m.includes('not_platform_admin') || code === '42501') {
    return "Votre compte n'est pas administrateur de la plateforme : le changement est refusé."
  }
  if (m.includes('extra_vehicles_not_offered')) {
    return 'Les véhicules supplémentaires ne sont proposés qu’aux paliers Starter et Pro : mettez 0 pour Business.'
  }
  if (m.includes('invalid_extra_vehicles')) return 'Nombre de véhicules supplémentaires invalide : un entier, 0 ou plus.'
  if (m.includes('invalid_billing_period')) return 'Période de facturation inconnue : choisissez mensuel ou annuel.'
  if (code === 'PGRST202' || m.includes('could not find the function')) {
    return "L'activation manuelle n'est pas encore installée sur la base (migrations 0019 et 0027 à pousser)."
  }
  if (code === 'PGRST301' || m.includes('jwt expired') || m.includes('jwt')) {
    return 'Session expirée — reconnectez-vous puis recommencez.'
  }
  if (code === '23503') return "Cette organisation n'existe plus. Rechargez la page."
  if (code === '22P02' || m.includes('invalid input value for enum')) {
    return "Valeur non reconnue pour le palier ou le statut. Rechargez la page."
  }
  if (m.includes('failed to fetch') || m.includes('load failed') || m.includes('networkerror')) {
    return 'Connexion impossible : vérifiez votre réseau puis réessayez. Rien n’a été enregistré.'
  }
  return `L'enregistrement a échoué${detail ? ` (${detail})` : message ? ` (${message})` : ''}. Rien n’a été confirmé.`
}

/** Erreur de lecture du journal `subscription_changes`. */
export function traduireErreurJournal(erreur: unknown): string {
  const { message, code } = lire(erreur)
  if (code === '42P01' || code === 'PGRST205' || message.toLowerCase().includes('subscription_changes')) {
    return "Le journal des changements n'est pas encore installé sur la base (migration 0019 à pousser)."
  }
  if (message.toLowerCase().includes('jwt')) return 'Session expirée — reconnectez-vous.'
  return `Historique illisible pour le moment${message ? ` (${message})` : ''}.`
}
